/**
 * Daily Matters — Moments section, mobile carousel.
 * Keeps the chip rail and the swipe carousel in step with the section's radio state:
 * tapping a chip checks its radio (native <label>), we scroll the matching card into view;
 * swiping the carousel checks the radio of the card that snapped into place.
 * Without this script the carousel still swipes and the chips still restyle; they just do not scroll each other.
 */
if (!customElements.get('dm-moments-carousel')) {
  class DmMomentsCarousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-dm-rail]');
      this.track = this.querySelector('[data-dm-track]');
      this.root = this.closest('.dm-moments') || this;
      this.radios = Array.from(this.root.querySelectorAll('input.dm-moments__radio'));
      if (!this.rail || !this.track || this.radios.length === 0) return;

      this.lock = null;
      this.onChange = (event) => {
        const index = this.radios.indexOf(event.target);
        if (index >= 0) this.go(index);
      };
      this.onScroll = () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
      };
      this.root.addEventListener('change', this.onChange);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      const checked = this.radios.findIndex((radio) => radio.checked);
      if (checked > 0 && this.isVisible()) {
        this.scrollChildIntoView(this.track, checked, false, 'auto');
        this.scrollChildIntoView(this.rail, checked, true, 'auto');
      }
    }

    disconnectedCallback() {
      if (this.root) this.root.removeEventListener('change', this.onChange);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.lockTimer);
    }

    isVisible() {
      return this.offsetParent !== null;
    }

    behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    scrollChildIntoView(container, index, center, behavior) {
      const child = container.children[index];
      if (!child) return;
      const pad = parseFloat(getComputedStyle(container).paddingLeft) || 0;
      const containerRect = container.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();
      let left = container.scrollLeft + (childRect.left - containerRect.left) - pad;
      if (center) left -= (containerRect.width - pad * 2 - childRect.width) / 2;
      container.scrollTo({ left: Math.max(0, left), behavior: behavior || this.behavior() });
    }

    go(index) {
      if (!this.isVisible()) return;
      this.lock = index;
      clearTimeout(this.lockTimer);
      this.lockTimer = setTimeout(() => {
        this.lock = null;
      }, 900);
      this.scrollChildIntoView(this.track, index, false);
      this.scrollChildIntoView(this.rail, index, true);
    }

    syncFromScroll() {
      const track = this.track;
      const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const base = track.getBoundingClientRect().left + pad;
      let best = 0;
      let bestDistance = Infinity;
      Array.from(track.children).forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - base);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      if (this.lock !== null) {
        if (best === this.lock) this.lock = null;
        return;
      }
      const radio = this.radios[best];
      if (radio && !radio.checked) {
        radio.checked = true;
        this.scrollChildIntoView(this.rail, best, true);
      }
    }
  }

  customElements.define('dm-moments-carousel', DmMomentsCarousel);
}
